/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionRequest,
  ActionSuccess,
  ActionFail,
  Recording,
  Asset,
  XError,
  TimeUtils,
  Time,
  PpvEntitlement,
  AssetMediaType,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, put, select, call, takeEvery, take, race } from 'redux-saga/effects';
import { Selectors as SingleRecordingsSelectors } from 'core/store/ducks/recordings/singleRecordings';
import * as Recordings from 'core/store/ducks/recordings';
import {
  Selectors as EntitlesAssetsSelectors,
  ActionType as EntitledAssetsActionTypes,
} from 'core/store/ducks/entitlements/assets/entitledAssets';
import { Selectors as PPVSelectors } from 'core/store/ducks/entitlements/ppv';
import AssetInfoUtils from 'store/utils/assetInfoUtils';
import Constants from 'configs/Constants';
import { StoreState } from 'store/store';

enum ActionType {
  GET_CONTINUE_WATCHING_ASSETS_REQUEST = 'myTV/continueWatching/GET_CONTINUE_WATCHING_ASSETS_REQUEST',
  GET_CONTINUE_WATCHING_ASSETS_SUCCESS = 'myTV/continueWatching/GET_CONTINUE_WATCHING_ASSETS_SUCCESS',
  GET_CONTINUE_WATCHING_ASSETS_FAIL = 'myTV/continueWatching/GET_CONTINUE_WATCHING_ASSETS_FAIL',
}

type GetContinueWatchingAssetsRequest = ActionRequest<ActionType.GET_CONTINUE_WATCHING_ASSETS_REQUEST>;
type GetContinueWatchingAssetsSuccess = ActionSuccess<ActionType.GET_CONTINUE_WATCHING_ASSETS_SUCCESS, Asset[]>;
type GetContinueWatchingAssetsFail = ActionFail<ActionType.GET_CONTINUE_WATCHING_ASSETS_FAIL>;

export type Actions =
  | GetContinueWatchingAssetsRequest
  | GetContinueWatchingAssetsSuccess
  | GetContinueWatchingAssetsFail;

const Actions = {
  getContinueWatchingAssetsRequest: (): GetContinueWatchingAssetsRequest => ({
    type: ActionType.GET_CONTINUE_WATCHING_ASSETS_REQUEST,
  }),
  getContinueWatchingAssetsSuccess: (assets: Asset[]): GetContinueWatchingAssetsSuccess => ({
    type: ActionType.GET_CONTINUE_WATCHING_ASSETS_SUCCESS,
    payload: assets,
  }),
  getContinueWatchingAssetsFail: (error: XError): GetContinueWatchingAssetsFail => ({
    type: ActionType.GET_CONTINUE_WATCHING_ASSETS_FAIL,
    error,
  }),
};

export const ActionCreators = {
  getContinueWatchingAssets: (): GetContinueWatchingAssetsRequest => Actions.getContinueWatchingAssetsRequest(),
};

export interface ReducerState {
  data: Asset[];
  processing?: boolean;
  processed?: boolean;
  error?: XError;
}

const initialState: ReducerState = {
  data: [],
};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.GET_CONTINUE_WATCHING_ASSETS_REQUEST:
      return {
        data: [],
        processing: true,
        processed: false,
      };
    case ActionType.GET_CONTINUE_WATCHING_ASSETS_SUCCESS:
      return {
        data: action.payload,
        processed: true,
        processing: false,
      };
    case ActionType.GET_CONTINUE_WATCHING_ASSETS_FAIL:
      return {
        data: [],
        processed: true,
        processing: false,
        error: action.error,
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getContinueWatchingAssets: (state: StoreState): Asset[] | undefined => state.myTv.continueWatchingAssets?.data,
  isWatchNextProcessed: (state: StoreState): boolean => !!state.myTv.continueWatchingAssets?.processed,
  watchNextError: (state: StoreState): boolean => !!state.myTv.continueWatchingAssets?.error,
};

/* Helping functions */
function* getEntitledAssets(): SagaIterator<Asset[]> {
  const isEntitledAssetsFinished = yield select(EntitlesAssetsSelectors.isProcessed);

  if (!isEntitledAssetsFinished) {
    yield take([EntitledAssetsActionTypes.SUCCESS, EntitledAssetsActionTypes.FAIL]);
  }

  return yield select(EntitlesAssetsSelectors.getAssets);
}

function* getSingleRecordingAssets(): SagaIterator<Recording[]> {
  const isRecordingsProcessed = yield select(Recordings.Selectors.isRecordingsProcessed);

  if (!isRecordingsProcessed) {
    yield race([take(Recordings.ActionTypes.SUCESS), take(Recordings.ActionTypes.FAIL)]);
  }

  return yield select(SingleRecordingsSelectors.getRecordings);
}

interface InfoTimeAsset {
  asset: Asset;
  timestamp: Time;
}

function* verifyEntitledAssetBookmark(
  asset: Asset,
  ppvEntitlements: PpvEntitlement[]
): SagaIterator<InfoTimeAsset | undefined> {
  let assetBookmark: Ducks.BookmarksCache.State | undefined;

  if (asset.mediaType !== AssetMediaType.FOLDER) {
    assetBookmark = yield select(Ducks.BookmarksCache.Selectors.getBookmarkState, asset);
  }

  let purchaseDate;

  for (let i = 0; i < ppvEntitlements.length; i += 1) {
    if (ppvEntitlements[i].mediaId === asset.id) {
      purchaseDate = ppvEntitlements[i].purchaseDate;
    }
  }

  const infoTimeAsset = {
    asset,
    timestamp: assetBookmark?.expires || purchaseDate || TimeUtils.now(),
  };

  return infoTimeAsset;
}

function* verifyRecordingBookmark(recording: Recording): SagaIterator<InfoTimeAsset | undefined> {
  const recordingBookmark: Ducks.BookmarksCache.State | undefined = yield select(
    Ducks.BookmarksCache.Selectors.getBookmarkState,
    recording as Asset
  );

  return {
    asset: recording,
    timestamp: recordingBookmark?.expires || (recording.endDate as Time) || TimeUtils.now(),
  };
}

function* orderEntitledAssets(entitledAssets: Asset[]): SagaIterator<InfoTimeAsset[]> {
  const ppvEntitlements = yield select(PPVSelectors.getPpvEntitlements);
  const orderedAssets: InfoTimeAsset[] = [];

  for (let i = 0; i < entitledAssets.length; i += 1) {
    const asset = yield call(verifyEntitledAssetBookmark, entitledAssets[i], ppvEntitlements);
    orderedAssets.push(asset);
  }

  const sortedAssets = orderedAssets.sort((a: InfoTimeAsset, b: InfoTimeAsset) => {
    return TimeUtils.compare(b.timestamp, a.timestamp);
  });

  return sortedAssets.reverse().slice(0, Constants.MAX_ASSETS_WATCH_NEXT);
}

function* orderRecordings(recordings: Recording[]): SagaIterator<InfoTimeAsset[]> {
  const orderedRecordings = yield all(
    recordings.map((recording) => {
      return call(verifyRecordingBookmark, recording);
    })
  );

  const sortedRecordings = orderedRecordings.sort((a: InfoTimeAsset, b: InfoTimeAsset) => {
    return TimeUtils.compare(b.timestamp, a.timestamp);
  });

  return sortedRecordings.reverse().slice(0, Constants.MAX_ASSETS_WATCH_NEXT);
}

function* orderCatchUp(bookmarks: Ducks.BookmarksCache.ReducerState): SagaIterator<InfoTimeAsset[]> {
  const entries = Object.entries(bookmarks);
  const retrievedAssets: InfoTimeAsset[] = [];

  for (let i = 0; i < entries.length; i += 1) {
    const element: [string, Ducks.BookmarksCache.State] = entries[i];
    let asset;
    if (element[1].data?.assetMediaType === AssetMediaType.EPG) {
      asset = yield call(AssetInfoUtils.getProgram, element[1].data.assetId);
      retrievedAssets.push({
        asset,
        timestamp: element[1].expires || TimeUtils.now(),
      });
    } else if (element[1].data?.assetMediaType === AssetMediaType.MOVIE) {
      asset = yield call(AssetInfoUtils.getMedia, element[1].data.assetId);
      retrievedAssets.push({
        asset,
        timestamp: element[1].expires || TimeUtils.now(),
      });
    }
  }

  const sortedCatchUp = retrievedAssets.sort((a: InfoTimeAsset, b: InfoTimeAsset) => {
    return TimeUtils.compare(b.timestamp, a.timestamp);
  });

  return sortedCatchUp.reverse().slice(0, Constants.MAX_ASSETS_WATCH_NEXT);
}

function* getContinueWatchingAssetsSaga(): SagaIterator {
  try {
    const bookmarks = yield select(Ducks.BookmarksCache.Selectors.getBookmarks);
    // Entitled Assets
    const entitledAssets = yield call(getEntitledAssets);
    const entitlementsWithTimestamp: InfoTimeAsset[] = yield call(orderEntitledAssets, entitledAssets);

    const recordingAssets: Recording[] = yield call(getSingleRecordingAssets);

    const recordingsWithTimestamp: InfoTimeAsset[] = yield call(orderRecordings, recordingAssets);

    // ir buscar os catch-up e part of subscription aos bookmarks
    // Bookmark type EPG e Media
    const catchUpWithTimestamp: InfoTimeAsset[] = yield call(orderCatchUp, bookmarks);

    const orderedAssets = [...entitlementsWithTimestamp, ...recordingsWithTimestamp, ...catchUpWithTimestamp];

    const sortedAssets = orderedAssets
      .sort((a: InfoTimeAsset, b: InfoTimeAsset) => {
        return TimeUtils.compare(b.timestamp, a.timestamp);
      })
      .slice(0, Constants.MAX_ASSETS_WATCH_NEXT)
      .map((asset: InfoTimeAsset) => {
        return asset.asset;
      });

    yield put(Actions.getContinueWatchingAssetsSuccess(sortedAssets));
  } catch (error) {
    yield put(Actions.getContinueWatchingAssetsFail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionType.GET_CONTINUE_WATCHING_ASSETS_REQUEST, getContinueWatchingAssetsSaga)]);
}
